<script setup lang="ts">
import { bannerService } from "@lxc/app-device-common";
import type { Ref } from "vue";
import { BANNER_CONTAINER_ID } from "~/core/constants/constants";
import type LxcError from "~/core/models/LxcError.class";
import { useGetActiveLicenseInformation } from "~/modules/license/composables/useGetActiveLicenseInformation";
import type { License } from "~/modules/license/models/License.interface";
import { getLicenseUploadErrors } from "~/modules/license/utils/getLicenseUploadErrors";

const props = defineProps<{
  organizationCode?: number;
}>();

const { isLoading, result, error, getActiveLicenseInformation } =
  useGetActiveLicenseInformation({
    organizationCode: props.organizationCode,
  });

const license: Ref<License | undefined> = ref();

/**
 * Fetch the license
 */
async function fetchLicense() {
  await getActiveLicenseInformation(true);
  if (error.value) {
    license.value = undefined;
  } else {
    license.value = result.value;
  }
}

/**
 * Display the error in the banner if the error is defined, otherwise hide the banner
 * @param error
 */
function displayError(error?: LxcError) {
  if (error) {
    bannerService.error({
      message: getLicenseUploadErrors(error),
      container: `#${BANNER_CONTAINER_ID}`,
      dangerouslyUseHTMLString: true,
    });
  } else {
    bannerService.hide();
  }
}

onMounted(fetchLicense);
</script>

<template>
  <template v-if="error">
    <license-error-message
      :organization-code="organizationCode"
      :error="error"
      @upload-success="fetchLicense"
      @upload-error="displayError"
    />
  </template>
  <container-component v-else :is-loading="isLoading" :error="error">
    <license-content
      :organization-code="organizationCode"
      :license="license"
      @upload-success="fetchLicense"
      @upload-error="displayError"
    />
  </container-component>
</template>
